import { Flex, Popconfirm, Tag, Tooltip } from 'antd';
import { AuthorizedIcon, Icon } from '../../components';
import TrashSolidBlack from '../../assets/icons/trash-solid-b-black.svg';
import PenToSquareSolidBlack from '../../assets/icons/pen-to-square-solid-w-black.svg';
import EyeRegularWBlack from '../../assets/icons/eye-regular-w-black.svg';
import Variant99Black from '../../assets/icons/variant99-black.svg';
import FolderOpenRegular from '../../assets/icons/folder-open-regular.svg';
import Subtract from '../../assets/icons/Subtract.svg';
import styles from './index.module.scss';
import { status_colors } from '../../assets/conts';
import { IEnum } from '../../services/type';
import {
  formatCurrencyTRY,
  formatISOString,
  getValueFromEnum,
} from '../../utils/functions';
import { ColumnType } from 'antd/es/table';
import { actionTypes } from '../../assets/enums';
import { IFile, IOrganization, IDamageManagementDataSource } from '../../types';

export const getColumns = (
  handleClickEye: (id: number) => void,
  handleClickDelete: (id: number) => Promise<void>,
  handleClickCancel: (id: number) => Promise<void>,
  handleOpenEditModal: (id: number) => Promise<void>,
  handleClickRemoveParent: (parent: number, id: number) => Promise<void>,
  handleSetAdditionalFile: (id: number, file: IFile[]) => void,
  organizations: IOrganization[],
  branches: IEnum[],
  cities: IEnum[],
  countries: IEnum[],
  currencies: IEnum[],
  damageTypes: IEnum[],
  statusChoices: IEnum[],
): Array<
  ColumnType<IDamageManagementDataSource> & { searchable?: boolean }
> => [
  {
    title: 'Şirket',
    dataIndex: 'organization',
    key: 'organization',
    searchable: true,
    filters: organizations.map((item) => ({
      text: item.name || '',
      value: item.id,
    })),
    width: 150,
    sorter: (a, b) => a.organization - b.organization,
    render: (_, record) => (
      <Tooltip
        title={
          organizations.find((item) => item.id === record.organization)?.name
        }
      >
        <span>{`${organizations.find((item) => item.id === record.organization)?.name.slice(0, 15)}...`}</span>
      </Tooltip>
    ),
  },
  {
    title: 'Dosya No',
    dataIndex: 'policy_number',
    key: 'policy_number',
    width: 150,
    searchable: true,
    sorter: (a, b) => a.policy_number - b.policy_number,
  },
  {
    title: 'Bağlı Dosya',
    dataIndex: 'parent',
    key: 'parent',
    width: 150,
    sorter: (a, b) => a.parent - b.parent,
  },
  {
    title: 'Hasar Tipi',
    dataIndex: 'damage_type',
    key: 'damage_type',
    width: 150,
    filters: damageTypes.map((item) => ({
      text: getValueFromEnum(damageTypes, item.key) || '',
      value: item.key,
    })),
    filterMode: 'tree',
    filterSearch: true,
    sorter: (a, b) => a.damage_type?.localeCompare(b.damage_type),
    render: (_: unknown, record: IDamageManagementDataSource) => (
      <Tag className={styles[`${record.damage_type}Tag`]}>
        {getValueFromEnum(damageTypes, record.damage_type)}
      </Tag>
    ),
  },
  {
    title: 'Branş',
    dataIndex: 'branch',
    key: 'branch',
    width: 150,
    filters: branches.map((item) => ({
      text: getValueFromEnum(branches, item.key) || '',
      value: item.key,
    })),
    filterMode: 'tree',
    filterSearch: true,
    sorter: (a, b) => a.branch?.localeCompare(b.branch),
    render: (_: unknown, record: IDamageManagementDataSource) => (
      <div>{getValueFromEnum(branches, record.branch)}</div>
    ),
  },
  {
    title: 'Hasar Yeri',
    dataIndex: 'country',
    key: 'country',
    width: 150,
    sorter: (a, b) => a.country?.localeCompare(b.country),
    render: (_: unknown, record: IDamageManagementDataSource) => (
      <div>{`${record.country ? getValueFromEnum(countries, record.country) : ''}${record.city ? ` / ${getValueFromEnum(cities, record.city)}` : ''} `}</div>
    ),
  },
  {
    title: 'İhbar Tarihi',
    dataIndex: 'notice_date',
    key: 'notice_date',
    width: 150,
    sorter: (a, b) =>
      new Date(a.notice_date).getTime() - new Date(b.notice_date).getTime(),
    render: (_, record) => (
      <div>
        {record.notice_date ? formatISOString(record.notice_date) : null}
      </div>
    ),
  },
  {
    title: 'Hasar Tarihi',
    dataIndex: 'damage_date',
    key: 'damage_date',
    width: 150,
    sorter: (a, b) =>
      new Date(a.damage_date).getTime() - new Date(b.damage_date).getTime(),
    render: (_, record) => (
      <div>
        {record.damage_date ? formatISOString(record.damage_date) : null}
      </div>
    ),
  },
  {
    title: 'Muallak Bedel',
    dataIndex: 'pending_loss',
    key: 'pending_loss',
    width: 150,
    sorter: (a, b) => (a.pending_loss || 0) - (b.pending_loss || 0),
    render: (_: unknown, record: IDamageManagementDataSource) => (
      <div>
        {formatCurrencyTRY(record.pending_loss)}{' '}
        {getValueFromEnum(currencies, record.currency)}
      </div>
    ),
  },
  {
    title: 'Durum',
    dataIndex: 'recent_status',
    key: 'recent_status',
    width: 150,
    filters: statusChoices.map((item) => ({
      text: getValueFromEnum(statusChoices, item.key) || '',
      value: item.key,
    })),
    filterMode: 'tree',
    filterSearch: true,
    sorter: (a, b) => a.recent_status?.localeCompare(b.recent_status),
    render: (_: unknown, record: IDamageManagementDataSource) => (
      <Tag color={status_colors[record.recent_status]}>
        {getValueFromEnum(statusChoices, record.recent_status)}
      </Tag>
    ),
  },
  {
    title: 'Açıklama',
    dataIndex: 'description',
    key: 'description',
    width: 150,
    sorter: (a, b) => a.description?.localeCompare(b.description),
    render: (_: unknown, record: IDamageManagementDataSource) => (
      <Tooltip title={record.description}>
        <span>{`${record.description?.slice(0, 10)}...`}</span>
      </Tooltip>
    ),
  },
  {
    title: 'Eksper',
    dataIndex: 'expert',
    key: 'expert',
    width: 150,
    sorter: (a, b) => a.expert?.localeCompare(b.expert),
  },
  {
    title: 'İşlem',
    dataIndex: 'action',
    key: 'action',
    width: 200,
    render: (_: unknown, record: IDamageManagementDataSource) => (
      <Flex justify="flex-end" gap={14}>
        {record.parent && (
          <AuthorizedIcon
            action="delete"
            model="parentdamagemanagement"
            source={Subtract}
            popoverText={actionTypes.DELETE_PARENT}
            onClick={() => handleClickRemoveParent(record.parent, record.id)}
          />
        )}
        <Icon
          popoverText={actionTypes.VIEW}
          source={EyeRegularWBlack}
          onClick={() => record?.id && handleClickEye(record.id)}
        />
        {record.recent_status !== 'cancelled' && (
          <Popconfirm
            title={
              <div className={styles.popconfirmTitle}>
                İptal işlemini onaylıyor musunuz?
              </div>
            }
            overlayInnerStyle={{
              width: 150,
              borderColor: '#D6222A',
              borderWidth: 1,
              borderStyle: 'solid',
            }}
            okText="Onayla"
            cancelText="İptal"
            icon={null}
            color="#FAFAFA"
            okButtonProps={{ danger: true, style: { width: '45%' } }}
            cancelButtonProps={{ danger: true, style: { width: '45%' } }}
            onConfirm={() => handleClickCancel(record.id)}
          >
            <AuthorizedIcon
              action="add"
              model="damagemanagement"
              popoverText={actionTypes.CANCEL}
              source={Variant99Black}
            />
          </Popconfirm>
        )}
        <Popconfirm
          title={
            <div className={styles.popconfirmTitle}>
              Silme işlemini onaylıyor musunuz?
            </div>
          }
          overlayInnerStyle={{
            width: 150,
            borderColor: '#D6222A',
            borderWidth: 1,
            borderStyle: 'solid',
          }}
          okText="Onayla"
          cancelText="İptal"
          icon={null}
          color="#FAFAFA"
          okButtonProps={{ danger: true, style: { width: '45%' } }}
          cancelButtonProps={{ danger: true, style: { width: '45%' } }}
          onConfirm={() => handleClickDelete(record.id)}
        >
          <AuthorizedIcon
            action="delete"
            model="damagemanagement"
            popoverText={actionTypes.DELETE}
            source={TrashSolidBlack}
          />
        </Popconfirm>
        <AuthorizedIcon
          action="change"
          model="damagemanagement"
          popoverText={actionTypes.CHANGE}
          source={PenToSquareSolidBlack}
          onClick={() => handleOpenEditModal(record.id)}
        />
        <AuthorizedIcon
          action="view"
          model="damagemanagementadditionalfile"
          popoverText={actionTypes.ADDITIONAL_FILE}
          source={FolderOpenRegular}
          onClick={() =>
            handleSetAdditionalFile(record.id, record.additional_files)
          }
        />
      </Flex>
    ),
  },
];
